import { useState } from 'react';
import { toMajor } from '../../contracts/money';
import { ChevronDown, Download, Wallet } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { formatDate, formatInt } from '../../lib/format';
import { downloadCsv } from '../../lib/csv';
import { useResellerRevenue } from '../../api/reseller';

const PERIODS = ['This year', 'Last 12 months', 'Last quarter', 'Last month'];
const CURRENCIES = ['BDT', 'USD'];

// reseller_payouts.method / .status are real columns. This table used to hardcode
// "Bank transfer" and a green "Paid" pill on EVERY row — so a payout that was still
// pending, deliberately held, or outright failed was shown to the reseller as money
// already in their account. The default method is bKash, so the label was usually
// wrong too.
const PAYOUT_METHOD_LABEL: Record<string, string> = {
  bank_transfer: 'Bank transfer', bkash: 'bKash', nagad: 'Nagad', manual: 'Manual',
};
const PAYOUT_STATUS_STYLE: Record<string, { label: string; pill: string; dot: string }> = {
  paid: {
    label: 'Paid',
    pill: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800',
    dot: 'bg-emerald-600 dark:bg-emerald-400',
  },
  pending: {
    label: 'Pending',
    pill: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800',
    dot: 'bg-amber-600 dark:bg-amber-400',
  },
  held: {
    label: 'Held',
    pill: 'bg-zinc-100 text-zinc-700 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-600',
    dot: 'bg-zinc-500 dark:bg-zinc-400',
  },
  failed: {
    label: 'Failed',
    pill: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-800',
    dot: 'bg-rose-600 dark:bg-rose-400',
  },
};

function PayoutStatusPill({ status }: { status: string }) {
  const s = PAYOUT_STATUS_STYLE[status] ?? PAYOUT_STATUS_STYLE.pending!;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${s.pill}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

/** Reseller revenue & commissions: KPI summary, monthly bar chart, payouts table. */
export function Revenue() {
  const [period, setPeriod] = useState<string>(PERIODS[0] ?? 'This year');
  const [currency, setCurrency] = useState<string>(CURRENCIES[0] ?? 'BDT');
  const { data: resellerRevenue } = useResellerRevenue();
  if (!resellerRevenue) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
        Loading revenue…
      </div>
    );
  }
  const max = Math.max(0, ...resellerRevenue.monthly.map((m) => m.bdt));
  // ITCity's cut as a % of gross (the rest is the reseller's commission).
  const platformPct = resellerRevenue.grossPaidMinor > 0
    ? Math.round((resellerRevenue.platformShareMinor / resellerRevenue.grossPaidMinor) * 100)
    : 20;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Reseller"
        title="Revenue & commissions"
        subtitle="What customers paid, what platform kept, and what's owed to you."
        action={
          <>
            <SelectChip value={period} options={PERIODS} onChange={setPeriod} />
            <SelectChip value={currency} options={CURRENCIES} onChange={setCurrency} />
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<Download className="w-4 h-4" />}
              onClick={() =>
                downloadCsv(
                  'reseller-revenue',
                  ['Month', 'Revenue (BDT)'],
                  resellerRevenue.monthly.map((m) => [m.month, toMajor(m.bdt)]),
                )
              }
            >
              <span className="hidden sm:inline">Export CSV</span>
            </Button>
          </>
        }
      />

      {/* KPI summary — percentages derived from the actual split, not hardcoded. */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
        <KpiTile label="Gross paid by customers" minor={resellerRevenue.grossPaidMinor} />
        <KpiTile label={`Platform fee (${platformPct}%)`} minor={resellerRevenue.platformShareMinor} muted />
        <KpiTile label={`Your commission (${100 - platformPct}%)`} minor={resellerRevenue.yourCommissionMinor} accent />
        <KpiTile label="Paid to you so far" minor={resellerRevenue.paidOutMinor} />
        <KpiTile
          label="Outstanding"
          minor={resellerRevenue.outstandingMinor}
          subtitle={`Next payout ${formatDate(resellerRevenue.nextPayoutAt, { compact: true })}`}
        />
      </div>

      {/* Monthly chart */}
      <Card className="p-5 sm:p-6 mb-6">
        <div className="flex items-end justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">Monthly revenue</h2>
            <p className="text-sm text-zinc-600 dark:text-zinc-300 mt-0.5">
              Last 12 months of customer payments routed through your reseller account.
            </p>
          </div>
        </div>
        <div className="flex items-end gap-2 sm:gap-3 h-56">
          {resellerRevenue.monthly.map((m) => {
            const pct = (m.bdt / max) * 100;
            return (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-2 group">
                <div className="relative w-full flex items-end h-44">
                  <div
                    className="w-full rounded-t-md bg-brand-subtle dark:bg-brand-dm-subtle/40 group-hover:bg-brand dark:group-hover:bg-brand-dm transition-colors relative"
                    style={{ height: `${pct}%` }}
                  >
                    <div className="absolute -top-7 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition pointer-events-none">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium tabular-nums bg-zinc-950 text-white dark:bg-zinc-50 dark:text-zinc-950 whitespace-nowrap">
                        ৳{formatInt(toMajor(m.bdt))}
                      </span>
                    </div>
                  </div>
                </div>
                <span className="text-[10px] sm:text-xs text-zinc-500 dark:text-zinc-400 tabular-nums tracking-tight">
                  {m.month.split(' ')[0]}
                </span>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Payouts */}
      <Card className="overflow-hidden">
        <div className="px-5 sm:px-6 py-4 flex items-end justify-between gap-3 border-b border-zinc-200 dark:border-zinc-700">
          <div>
            <div className="text-base font-semibold text-zinc-950 dark:text-zinc-50">Recent payouts</div>
            <p className="text-sm text-zinc-600 dark:text-zinc-300 mt-0.5">
              Bank settlements made to your account.
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            iconLeft={<Download className="w-4 h-4" />}
            onClick={() =>
              downloadCsv(
                'reseller-payouts',
                ['Date', 'Amount (BDT)', 'Method', 'Bank reference', 'Status'],
                resellerRevenue.payouts.map((p) => [
                  formatDate(p.date), toMajor(p.bdt),
                  PAYOUT_METHOD_LABEL[p.method] ?? p.method,
                  p.bankRef,
                  PAYOUT_STATUS_STYLE[p.status]?.label ?? p.status,
                ]),
              )
            }
          >
            <span className="hidden sm:inline">Export CSV</span>
          </Button>
        </div>

        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Date</th>
                <th className="text-right font-medium px-4 py-3">Amount</th>
                <th className="text-left font-medium px-4 py-3">Method</th>
                <th className="text-left font-medium px-4 py-3">Bank reference</th>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {resellerRevenue.payouts.map((p) => (
                <tr key={p.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                  <td className="px-5 sm:px-6 py-4 text-zinc-700 dark:text-zinc-200 whitespace-nowrap tabular-nums">
                    {formatDate(p.date)}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <Money minor={p.bdt} hideCents tone="positive" className="font-medium" />
                  </td>
                  <td className="px-4 py-4 text-zinc-700 dark:text-zinc-200">
                    <span className="inline-flex items-center gap-1.5">
                      <Wallet className="w-3.5 h-3.5 text-zinc-500 dark:text-zinc-400" />
                      {PAYOUT_METHOD_LABEL[p.method] ?? p.method}
                    </span>
                  </td>
                  <td className="px-4 py-4 mono text-xs text-zinc-600 dark:text-zinc-300">{p.bankRef || '—'}</td>
                  <td className="px-5 sm:px-6 py-4">
                    <PayoutStatusPill status={p.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function KpiTile({
  label,
  minor,
  subtitle,
  muted = false,
  accent = false,
}: {
  label: string;
  minor: number;
  subtitle?: string;
  muted?: boolean;
  accent?: boolean;
}) {
  return (
    <Card className="p-4 sm:p-5">
      <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{label}</div>
      <div className="mt-1.5 text-xl sm:text-2xl font-semibold">
        <Money
          minor={minor}
          hideCents
          tone={accent ? 'positive' : 'neutral'}
          className={accent ? '' : muted ? 'text-zinc-600 dark:text-zinc-300' : 'text-zinc-950 dark:text-zinc-50'}
        />
      </div>
      {subtitle && <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{subtitle}</div>}
    </Card>
  );
}

function SelectChip({
  value,
  options,
  onChange,
}: {
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-3 pr-9 py-2 rounded-md text-xs font-medium bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand dark:focus:ring-brand-dm appearance-none"
      >
        {options.map((o) => (
          <option key={o}>{o}</option>
        ))}
      </select>
      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400 pointer-events-none" />
    </div>
  );
}
